import React, { useState } from 'react';
import styled from 'styled-components';
import {
    Badge,
    Button,
    Input,
    ConfirmDialog,
    gtagEvent,
    vars,
    formatPhone,
    formatCpfCnpj,
    onlyDigits,
} from '@agendor/ui';
import {
    FormGroup,
    InputGroup,
    Checkbox,
    CheckboxLabel,
    LabelRow,
    WarningBanner,
    HelperText,
    Select,
} from '../components/Input';
import { Modal } from '../components/Modal';
import { PlanCatalogue } from '@agendor/shared';
import BusinessService, {
    BookingPaymentMethod,
    BusinessDetails,
    deriveCommercialInfoStatus,
    hasPaidAccess,
} from '../business-service';
import { useSingleModalRoute } from '../navigation';
import { useToast } from '../toast-context';

const BOOKING_PAYMENT_METHOD_LABELS: Record<BookingPaymentMethod, string> = {
    pix: 'Somente Pix',
    card: 'Cartão de crédito ou débito',
    boleto: 'Boleto ou Pix',
    any: 'Pix, cartão ou boleto',
};

// Upsell styles
const UpsellContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 800px;
    margin: 0 auto;
    padding: 48px 24px;
    text-align: center;
`;

const IconWrapper = styled.div`
    width: 80px;
    height: 80px;
    border-radius: 20px;
    background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 32px;
`;

const PaymentIcon = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="white">
        <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
    </svg>
);

const UpsellTitle = styled.h1`
    font-size: 1.75rem;
    color: ${vars.foregroundColor};
    margin: 0 0 12px 0;
    font-weight: 700;
    line-height: 1.3;
`;

const UpsellSubtitle = styled.p`
    font-size: 1.1rem;
    color: #666;
    margin: 0 0 40px 0;
    line-height: 1.6;
`;

const FeaturesGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    width: 100%;
    margin-bottom: 40px;
    text-align: left;

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const FeatureCard = styled.div`
    background: #fff;
    border: 1px solid ${vars.dividerColor};
    border-radius: 12px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const FeatureIcon = styled.span`
    font-size: 1.5rem;
    line-height: 1;
`;

const FeatureTitle = styled.span`
    font-weight: 600;
    color: ${vars.foregroundColor};
    font-size: 0.95rem;
`;

const FeatureDescription = styled.span`
    font-size: 0.85rem;
    color: #666;
    line-height: 1.4;
`;

const CTACard = styled.div`
    background: ${vars.primaryColorLight};
    border: 1px solid ${vars.dividerColor};
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: 100%;
    max-width: 320px;
`;

const PriceRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 4px;
`;

const PriceValue = styled.span`
    font-size: 1.5rem;
    font-weight: 700;
    color: ${vars.foregroundColor};
`;

const PriceUnit = styled.span`
    font-size: 0.9rem;
    color: ${vars.foregroundColor};
`;

const PriceNote = styled.span`
    font-size: 0.85rem;
    color: #666;
`;

// Config styles
const ConfigContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
    max-width: 800px;
    margin: 0 auto;
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 24px;
`;

const SectionTitle = styled.h1`
    font-size: 1.5rem;
    color: ${vars.foregroundColor};
    margin: 0;
    font-weight: 600;
`;

const CardsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ConfigCard = styled.div`
    background-color: #fff;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    cursor: ${(p) => (p.onClick ? 'pointer' : 'default')};

    &:hover {
        ${(p) =>
            p.onClick ? `border-color: ${vars.foregroundColor}; background-color: ${vars.backgroundColorBright};` : ''}
    }

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
`;

const ConfigInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ConfigTitle = styled.h3`
    margin: 0;
    font-size: 1.1rem;
    color: ${vars.foregroundColor};
    font-weight: 600;
`;

const ConfigSummary = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    color: #666;
    font-size: 0.9rem;
`;

const SummaryItem = styled.span`
    color: #666;
`;

const features = [
    {
        icon: '\u{1F4B3}',
        title: 'Cobrança automática',
        description: 'Ao agendar, o cliente recebe um link de pagamento automaticamente.',
    },
    {
        icon: '\u{23F1}\u{FE0F}',
        title: 'Cancelamento automático',
        description: 'Se o cliente não pagar no prazo, o agendamento é cancelado automaticamente.',
    },
    {
        icon: '\u{1F4F1}',
        title: 'Funciona em todos os canais',
        description: 'Pagamento disponível no link de agendamento e no WhatsApp.',
    },
    {
        icon: '\u{1F512}',
        title: 'Pagamento seguro via Asaas',
        description: 'PIX, cartão de crédito, boleto e mais formas de pagamento.',
    },
];

type PaymentsSectionProps = {
    business: BusinessDetails | null;
    service: BusinessService | null;
    onReloadBusiness: () => Promise<void> | void;
    isLoadingBusiness: boolean;
    businessLoadError: string | null;
    isActive: boolean;
    modalSegments: string[];
    onNavigate: (segments: string[], replace?: boolean) => void;
    onNavigateToUpgrade: () => void;
};

export default function PaymentsSection({
    business,
    service,
    onReloadBusiness,
    isLoadingBusiness,
    businessLoadError,
    isActive,
    modalSegments,
    onNavigate,
    onNavigateToUpgrade,
}: PaymentsSectionProps) {
    const showConfigView = (business && hasPaidAccess(business)) ?? false;
    const { showToast } = useToast();

    const asaasModal = useSingleModalRoute({
        isActive,
        ready: true,
        segments: modalSegments,
        onNavigate,
        modalKey: 'asaas',
    });

    const bookingPaymentModal = useSingleModalRoute({
        isActive,
        ready: true,
        segments: modalSegments,
        onNavigate,
        modalKey: 'booking-payment',
    });

    const commercialInfoModal = useSingleModalRoute({
        isActive,
        ready: true,
        segments: modalSegments,
        onNavigate,
        modalKey: 'commercial-info',
    });

    const [commercialInfoDraft, setCommercialInfoDraft] = useState<{
        email: string;
        mobilePhone: string;
        postalCode: string;
        address: string;
        addressNumber: string;
        province: string;
        incomeValue: string;
        birthDate: string;
    } | null>(null);
    const [isLoadingCommercialInfo, setIsLoadingCommercialInfo] = useState(false);
    const [asaasDraft, setAsaasDraft] = useState<{ enabled: boolean; token: string }>({ enabled: false, token: '' });
    const [subAccountDraft, setSubAccountDraft] = useState({
        name: '',
        email: '',
        cpfCnpj: '',
        birthDate: '',
        mobilePhone: '',
        postalCode: '',
        address: '',
        addressNumber: '',
        province: '',
        incomeValue: '',
    });
    const [paymentDraft, setPaymentDraft] = useState<{
        percentage: number | null;
        timeoutMinutes: number | null;
        transferDeadlineHours: number | null;
        method: BookingPaymentMethod;
    }>({
        percentage: null,
        timeoutMinutes: null,
        transferDeadlineHours: null,
        method: 'pix',
    });
    const [isSaving, setIsSaving] = useState(false);
    const [showReplaceAccountConfirm, setShowReplaceAccountConfirm] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const asaasSubaccount = business?.asaasSubaccount;
    const isSubaccountMode = asaasSubaccount !== undefined;
    const hasSubaccount = asaasSubaccount != null;
    const commercialInfoExpirationDate = asaasSubaccount?.expirationDate ?? null;
    const commercialInfoStatus = deriveCommercialInfoStatus(commercialInfoExpirationDate);

    const handleOpenAsaas = () => {
        gtagEvent('payments_asaas_config_click');
        if (isSubaccountMode && !business?.hasAsaasToken) {
            setSubAccountDraft({
                name: '',
                email: '',
                cpfCnpj: '',
                birthDate: new Date().toISOString().slice(0, 10),
                mobilePhone: '',
                postalCode: '',
                address: '',
                addressNumber: '',
                province: '',
                incomeValue: '',
            });
            setAcceptedTerms(false);
        } else if (!isSubaccountMode) {
            setAsaasDraft({ enabled: !!business?.hasAsaasToken, token: '' });
        }
        asaasModal.open();
    };

    const handleOpenCommercialInfo = async () => {
        if (!service) return;
        setIsLoadingCommercialInfo(true);
        setCommercialInfoDraft(null);
        commercialInfoModal.open();
        try {
            const info = await service.getAsaasCommercialInfo();
            setCommercialInfoDraft({
                email: info.email,
                mobilePhone: info.mobilePhone,
                postalCode: info.postalCode,
                address: info.address,
                addressNumber: info.addressNumber,
                province: info.province,
                incomeValue: String(info.incomeValue),
                birthDate: info.birthDate,
            });
        } catch {
            showToast('Não foi possível carregar os dados cadastrais.', 'error', 'modal');
            commercialInfoModal.close();
        } finally {
            setIsLoadingCommercialInfo(false);
        }
    };

    const saveCommercialInfo = async () => {
        if (!service || !commercialInfoDraft) return;
        setIsSaving(true);
        try {
            await service.updateAsaasCommercialInfo({
                ...commercialInfoDraft,
                incomeValue: Number(commercialInfoDraft.incomeValue),
            });
            await onReloadBusiness();
            showToast('Dados cadastrais atualizados com sucesso!', 'success', 'app');
            commercialInfoModal.close();
        } catch (error: any) {
            if (error?.code === 'asaas_error') {
                const description = error.errors?.[0]?.description ?? 'Erro ao atualizar dados.';
                showToast(description, 'error', 'modal');
            } else {
                showToast('Não foi possível atualizar os dados cadastrais.', 'error', 'modal');
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleOpenBookingPayment = () => {
        gtagEvent('payments_booking_config_click');
        setPaymentDraft({
            percentage: business?.bookingPayment?.percentage ?? null,
            timeoutMinutes: business?.bookingPayment?.timeoutMinutes ?? null,
            transferDeadlineHours: business?.bookingPayment?.transferDeadlineHours ?? null,
            method: business?.bookingPayment?.method ?? 'pix',
        });
        bookingPaymentModal.open();
    };

    const saveSubAccount = async () => {
        if (!service) return;
        const parsedIncomeValue = Number(subAccountDraft.incomeValue);
        setIsSaving(true);
        try {
            await service.createAsaasSubAccount({ ...subAccountDraft, incomeValue: parsedIncomeValue });
            await onReloadBusiness();
            showToast('Conta de pagamentos ativada com sucesso!', 'success', 'app');
            asaasModal.close();
        } catch (error: any) {
            if (error?.code === 'already_configured') {
                showToast('A conta de pagamentos já está configurada.', 'error', 'modal');
            } else if (error?.code === 'asaas_error') {
                const description = error.errors?.[0]?.description ?? 'Erro ao criar conta.';
                showToast(description, 'error', 'modal');
            } else {
                showToast('Não foi possível ativar a conta de pagamentos.', 'error', 'modal');
            }
        } finally {
            setIsSaving(false);
        }
    };

    const saveAsaas = async (allowLosingLinkedCustomers?: boolean) => {
        if (!service) return;
        if (asaasDraft.enabled && !asaasDraft.token) {
            showToast('Insira a chave API para conectar o Asaas.', 'error', 'modal');
            return;
        }
        setIsSaving(true);
        try {
            const token = asaasDraft.enabled ? asaasDraft.token || null : null;
            await service.setAsaas({ token, allowLosingLinkedCustomers });
            await onReloadBusiness();
            showToast('Configuração Asaas salva com sucesso!', 'success', 'app');
            asaasModal.close();
        } catch (error: any) {
            if (error?.code === 'has_linked_customers') {
                setShowReplaceAccountConfirm(true);
            } else if (error?.code === 'invalid_token') {
                showToast('Chave API inválida. Verifique e tente novamente.', 'error', 'modal');
            } else {
                showToast('Não foi possível salvar a configuração Asaas.', 'error', 'modal');
            }
        } finally {
            setIsSaving(false);
        }
    };

    const canSaveSubAccount =
        !isSaving &&
        !!subAccountDraft.name &&
        !!subAccountDraft.email &&
        !!subAccountDraft.cpfCnpj &&
        !!subAccountDraft.birthDate &&
        !!subAccountDraft.mobilePhone &&
        !!subAccountDraft.postalCode &&
        !!subAccountDraft.address &&
        !!subAccountDraft.addressNumber &&
        !!subAccountDraft.province &&
        !!subAccountDraft.incomeValue &&
        acceptedTerms;

    const canSaveCommercialInfo =
        !isSaving &&
        !isLoadingCommercialInfo &&
        !!commercialInfoDraft?.email &&
        !!commercialInfoDraft?.mobilePhone &&
        !!commercialInfoDraft?.postalCode &&
        !!commercialInfoDraft?.address &&
        !!commercialInfoDraft?.addressNumber &&
        !!commercialInfoDraft?.province &&
        !!commercialInfoDraft?.incomeValue &&
        !!commercialInfoDraft?.birthDate;

    const canSaveAsaas =
        !isSaving && ((asaasDraft.enabled && !!asaasDraft.token) || (!asaasDraft.enabled && !!business?.hasAsaasToken));

    const removeAsaasToken = async (allowLosingLinkedCustomers?: boolean) => {
        if (!service) return;
        setIsSaving(true);
        try {
            await service.setAsaas({ token: null, allowLosingLinkedCustomers });
            await onReloadBusiness();
            showToast('Configuração de pagamentos removida.', 'success', 'app');
            asaasModal.close();
        } catch (error: any) {
            if (error?.code === 'has_linked_customers') {
                setShowReplaceAccountConfirm(true);
            } else {
                showToast('Não foi possível remover a configuração.', 'error', 'modal');
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveAsaas = () => (isSubaccountMode ? saveSubAccount() : saveAsaas());

    const handleConfirmReplaceAccount = async () => {
        setShowReplaceAccountConfirm(false);
        if (isSubaccountMode && business?.hasAsaasToken && !hasSubaccount) {
            await removeAsaasToken(true);
        } else {
            await saveAsaas(true);
        }
    };

    const handleSaveBookingPayment = async () => {
        if (!service) return;
        const { percentage, timeoutMinutes, transferDeadlineHours, method } = paymentDraft;

        if (percentage !== null && (percentage < 1 || percentage > 100)) {
            showToast('Porcentagem deve ser entre 1 e 100.', 'error', 'modal');
            return;
        }
        if (timeoutMinutes !== null && timeoutMinutes < 1) {
            showToast('Timeout deve ser pelo menos 1 minuto.', 'error', 'modal');
            return;
        }
        if (transferDeadlineHours !== null && transferDeadlineHours < 1) {
            showToast('Prazo de antecedência deve ser pelo menos 1 hora.', 'error', 'modal');
            return;
        }

        setIsSaving(true);
        try {
            await service.setBookingPayment({ percentage, timeoutMinutes, transferDeadlineHours, method });
            await onReloadBusiness();
            showToast('Configuração de pré-pagamento salva com sucesso!', 'success', 'app');
            bookingPaymentModal.close();
        } catch {
            showToast('Não foi possível salvar as configurações de pré-pagamento.', 'error', 'modal');
        } finally {
            setIsSaving(false);
        }
    };

    if (businessLoadError) {
        return (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                <div style={{ marginBottom: '12px', color: vars.foregroundColor }}>{businessLoadError}</div>
                <Button onClick={() => onReloadBusiness()} disabled={isLoadingBusiness}>
                    {isLoadingBusiness ? 'Carregando...' : 'Tentar novamente'}
                </Button>
            </div>
        );
    }

    if (!business) {
        return <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Carregando pagamentos...</div>;
    }

    if (!showConfigView) {
        return (
            <UpsellContainer data-testid="payments-upsell">
                <IconWrapper>
                    <PaymentIcon />
                </IconWrapper>
                <UpsellTitle>Receba antes de atender</UpsellTitle>
                <UpsellSubtitle>
                    Cobre uma porcentagem do serviço no momento do agendamento. Reduza faltas e garanta sua receita.
                </UpsellSubtitle>
                <FeaturesGrid>
                    {features.map((feature) => (
                        <FeatureCard key={feature.title}>
                            <FeatureIcon>{feature.icon}</FeatureIcon>
                            <FeatureTitle>{feature.title}</FeatureTitle>
                            <FeatureDescription>{feature.description}</FeatureDescription>
                        </FeatureCard>
                    ))}
                </FeaturesGrid>
                <CTACard>
                    {business?.subscription?.isTrialEligible && <Badge tone="highlight">7 dias grátis</Badge>}
                    <PriceNote>incluído nos planos Solo, Equipe e Escala, a partir de</PriceNote>
                    <PriceRow>
                        <PriceValue>{PlanCatalogue.formatPrice(PlanCatalogue.PAID.solo.priceInCents)}</PriceValue>
                        <PriceUnit>/mês</PriceUnit>
                    </PriceRow>
                    <Button
                        primary
                        onClick={() => {
                            gtagEvent('payments_upsell_upgrade_click');
                            onNavigateToUpgrade();
                        }}
                    >
                        {business?.subscription?.isTrialEligible ? 'Começar teste grátis' : 'Reativar'}
                    </Button>
                </CTACard>
            </UpsellContainer>
        );
    }

    return (
        <ConfigContainer>
            <Header>
                <SectionTitle>Pagamentos</SectionTitle>
            </Header>
            <CardsContainer>
                <ConfigCard onClick={handleOpenAsaas}>
                    <ConfigInfo>
                        <ConfigTitle>{isSubaccountMode ? 'Conta de pagamentos' : 'Conta Asaas'}</ConfigTitle>
                        <ConfigSummary>
                            {business?.hasAsaasToken ? (
                                <SummaryItem>
                                    {isSubaccountMode
                                        ? commercialInfoStatus === 'expired'
                                            ? 'Conta bloqueada \u2014 atualize seus dados cadastrais'
                                            : 'Conta ativa'
                                        : 'Chave API configurada'}
                                </SummaryItem>
                            ) : isSubaccountMode ? (
                                <SummaryItem>Ative sua conta para receber pagamentos</SummaryItem>
                            ) : (
                                <SummaryItem>Conecte sua conta Asaas para receber pagamentos</SummaryItem>
                            )}
                        </ConfigSummary>
                        {commercialInfoStatus !== 'ok' && (
                            <WarningBanner $severity={commercialInfoStatus === 'expired' ? 'error' : 'warning'}>
                                {commercialInfoStatus === 'expired'
                                    ? 'Seus dados cadastrais expiraram e os pagamentos estão bloqueados.'
                                    : `Seus dados cadastrais vencem em breve${
                                          commercialInfoExpirationDate
                                              ? ` (${new Date(commercialInfoExpirationDate + 'T00:00:00').toLocaleDateString('pt-BR')})`
                                              : ''
                                      }.`}{' '}
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleOpenCommercialInfo();
                                    }}
                                    style={{ color: 'inherit', fontWeight: 600 }}
                                >
                                    {commercialInfoStatus === 'expired' ? 'Atualizar agora' : 'Atualizar dados'}
                                </a>
                            </WarningBanner>
                        )}
                    </ConfigInfo>
                </ConfigCard>

                <ConfigCard onClick={handleOpenBookingPayment}>
                    <ConfigInfo>
                        <ConfigTitle>Pré-pagamento</ConfigTitle>
                        <ConfigSummary>
                            {business?.bookingPayment ? (
                                <>
                                    <SummaryItem>
                                        Porcentagem cobrada: {business.bookingPayment.percentage}%
                                    </SummaryItem>
                                    <SummaryItem>
                                        Forma de pagamento:{' '}
                                        {BOOKING_PAYMENT_METHOD_LABELS[business.bookingPayment.method]}
                                    </SummaryItem>
                                    {business.bookingPayment.timeoutMinutes && (
                                        <SummaryItem>
                                            Prazo para pagamento: {business.bookingPayment.timeoutMinutes} min
                                        </SummaryItem>
                                    )}
                                    {business.bookingPayment.transferDeadlineHours !== null && (
                                        <SummaryItem>
                                            Transferência/estorno: {business.bookingPayment.transferDeadlineHours}h de
                                            antecedência
                                        </SummaryItem>
                                    )}
                                </>
                            ) : (
                                <SummaryItem>Configure a cobrança antecipada para agendamentos</SummaryItem>
                            )}
                        </ConfigSummary>
                    </ConfigInfo>
                </ConfigCard>
            </CardsContainer>

            <Modal
                isOpen={asaasModal.isOpen}
                onClose={asaasModal.close}
                title={
                    hasSubaccount
                        ? 'Conta de pagamentos'
                        : isSubaccountMode && business?.hasAsaasToken
                          ? 'Conta Asaas'
                          : isSubaccountMode
                            ? 'Ativar conta de pagamentos'
                            : 'Conta Asaas'
                }
                footer={
                    hasSubaccount ? (
                        <Button onClick={asaasModal.close}>Fechar</Button>
                    ) : isSubaccountMode && business?.hasAsaasToken ? (
                        <>
                            <Button onClick={asaasModal.close} disabled={isSaving}>
                                Cancelar
                            </Button>
                            <Button primary onClick={() => removeAsaasToken()} disabled={isSaving}>
                                {isSaving ? 'Removendo...' : 'Remover'}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={asaasModal.close} disabled={isSaving}>
                                Cancelar
                            </Button>
                            <Button
                                primary
                                onClick={handleSaveAsaas}
                                disabled={isSubaccountMode ? !canSaveSubAccount : !canSaveAsaas}
                            >
                                {isSaving ? 'Salvando...' : isSubaccountMode ? 'Ativar' : 'Salvar'}
                            </Button>
                        </>
                    )
                }
            >
                {hasSubaccount ? (
                    <FormGroup>
                        <span style={{ color: vars.foregroundColor }}>
                            {commercialInfoStatus === 'expired'
                                ? 'Sua conta de pagamentos está bloqueada. Atualize seus dados cadastrais para reativar os pagamentos.'
                                : 'Sua conta de pagamentos está ativa. Os pagamentos dos seus clientes são processados automaticamente.'}
                        </span>
                        {commercialInfoStatus !== 'ok' && (
                            <Button
                                primary
                                style={{ alignSelf: 'flex-start' }}
                                onClick={() => {
                                    asaasModal.close();
                                    handleOpenCommercialInfo();
                                }}
                            >
                                Atualizar dados cadastrais
                            </Button>
                        )}
                    </FormGroup>
                ) : isSubaccountMode && business?.hasAsaasToken ? (
                    <FormGroup>
                        <span style={{ color: vars.foregroundColor }}>
                            Existe uma chave API configurada manualmente. Remova-a para poder ativar a conta de
                            pagamentos integrada.
                        </span>
                    </FormGroup>
                ) : isSubaccountMode ? (
                    <FormGroup>
                        <span style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
                            Para receber pagamentos dos seus clientes, vamos criar uma conta no Asaas, instituição de
                            pagamento regulamentada pelo Banco Central. Seus dados são necessários por exigência
                            regulatória.
                        </span>
                        <InputGroup>
                            <LabelRow>Nome completo</LabelRow>
                            <Input
                                value={subAccountDraft.name}
                                onChange={(e) => setSubAccountDraft((prev) => ({ ...prev, name: e.target.value }))}
                                placeholder="Nome do responsável"
                            />
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>E-mail</LabelRow>
                            <Input
                                type="email"
                                value={subAccountDraft.email}
                                onChange={(e) => setSubAccountDraft((prev) => ({ ...prev, email: e.target.value }))}
                                placeholder="email@exemplo.com"
                            />
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>CPF ou CNPJ</LabelRow>
                            <Input
                                value={formatCpfCnpj(subAccountDraft.cpfCnpj)}
                                onChange={(e) =>
                                    setSubAccountDraft((prev) => ({
                                        ...prev,
                                        cpfCnpj: onlyDigits(e.target.value).slice(0, 14),
                                    }))
                                }
                                placeholder="000.000.000-00"
                                maxLength={18}
                            />
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>Data de nascimento</LabelRow>
                            <Input
                                type="date"
                                value={subAccountDraft.birthDate}
                                onChange={(e) => setSubAccountDraft((prev) => ({ ...prev, birthDate: e.target.value }))}
                            />
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>Celular</LabelRow>
                            <Input
                                value={formatPhone(subAccountDraft.mobilePhone)}
                                onChange={(e) =>
                                    setSubAccountDraft((prev) => ({
                                        ...prev,
                                        mobilePhone: onlyDigits(e.target.value).slice(0, 11),
                                    }))
                                }
                                placeholder="(00) 00000-0000"
                                maxLength={15}
                            />
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>CEP</LabelRow>
                            <Input
                                value={onlyDigits(subAccountDraft.postalCode).replace(/(\d{5})(\d)/, '$1-$2')}
                                onChange={(e) =>
                                    setSubAccountDraft((prev) => ({
                                        ...prev,
                                        postalCode: onlyDigits(e.target.value).slice(0, 8),
                                    }))
                                }
                                placeholder="00000-000"
                                maxLength={9}
                            />
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>Endereço</LabelRow>
                            <Input
                                value={subAccountDraft.address}
                                onChange={(e) => setSubAccountDraft((prev) => ({ ...prev, address: e.target.value }))}
                                placeholder="Rua, Avenida..."
                            />
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>Número</LabelRow>
                            <Input
                                value={subAccountDraft.addressNumber}
                                onChange={(e) =>
                                    setSubAccountDraft((prev) => ({ ...prev, addressNumber: e.target.value }))
                                }
                                placeholder="123"
                            />
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>Bairro</LabelRow>
                            <Input
                                value={subAccountDraft.province}
                                onChange={(e) => setSubAccountDraft((prev) => ({ ...prev, province: e.target.value }))}
                                placeholder="Bairro"
                            />
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>Renda mensal</LabelRow>
                            <Input
                                type="number"
                                min="1"
                                value={subAccountDraft.incomeValue}
                                onChange={(e) =>
                                    setSubAccountDraft((prev) => ({ ...prev, incomeValue: e.target.value }))
                                }
                                placeholder="5000"
                            />
                        </InputGroup>
                        <InputGroup>
                            <CheckboxLabel>
                                <Checkbox
                                    data-testid="accept-terms"
                                    checked={acceptedTerms}
                                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                                />
                                Li e aceito os{' '}
                                <a
                                    href="https://central.ajuda.asaas.com/hc/pt-br/articles/32096847160859-Termos-e-Condi%C3%A7%C3%B5es-de-Uso"
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ color: 'inherit', textDecoration: 'underline' }}
                                >
                                    Termos de Uso do Asaas
                                </a>
                            </CheckboxLabel>
                        </InputGroup>
                    </FormGroup>
                ) : (
                    <FormGroup>
                        <InputGroup>
                            <CheckboxLabel>
                                <Checkbox
                                    checked={asaasDraft.enabled}
                                    onChange={(e) => setAsaasDraft((prev) => ({ ...prev, enabled: e.target.checked }))}
                                />
                                Conectar conta Asaas
                            </CheckboxLabel>
                            {asaasDraft.enabled && (
                                <>
                                    <LabelRow>Chave API</LabelRow>
                                    <Input
                                        type="password"
                                        value={asaasDraft.token}
                                        onChange={(e) => setAsaasDraft((prev) => ({ ...prev, token: e.target.value }))}
                                        placeholder={business?.hasAsaasToken ? '••••••••••••' : 'Insira sua chave API'}
                                    />
                                    <span style={{ fontSize: '0.8rem', color: '#888' }}>
                                        {business?.hasAsaasToken
                                            ? 'Deixe em branco para manter a chave atual.'
                                            : 'Encontre em sua conta Asaas > Integrações.'}
                                    </span>
                                </>
                            )}
                        </InputGroup>
                    </FormGroup>
                )}
            </Modal>

            <Modal
                isOpen={bookingPaymentModal.isOpen}
                onClose={bookingPaymentModal.close}
                title="Pré-pagamento"
                data-testid="booking-payment-modal"
                footer={
                    <>
                        <Button onClick={bookingPaymentModal.close} disabled={isSaving}>
                            Cancelar
                        </Button>
                        <Button primary onClick={handleSaveBookingPayment} disabled={isSaving}>
                            {isSaving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </>
                }
            >
                <FormGroup>
                    <InputGroup>
                        <CheckboxLabel>
                            <Checkbox
                                checked={paymentDraft.percentage !== null}
                                onChange={(e) =>
                                    setPaymentDraft((prev) => ({
                                        ...prev,
                                        percentage: e.target.checked ? (prev.percentage ?? 50) : null,
                                        timeoutMinutes: e.target.checked ? prev.timeoutMinutes : null,
                                        transferDeadlineHours: e.target.checked ? prev.transferDeadlineHours : null,
                                    }))
                                }
                            />
                            Cobrar no agendamento
                        </CheckboxLabel>
                        {paymentDraft.percentage !== null && (
                            <>
                                <LabelRow>Porcentagem (%)</LabelRow>
                                <Input
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={paymentDraft.percentage}
                                    onChange={(e) =>
                                        setPaymentDraft((prev) => ({ ...prev, percentage: Number(e.target.value) }))
                                    }
                                />
                                <LabelRow>Forma de pagamento</LabelRow>
                                <Select
                                    data-testid="booking-payment-method"
                                    value={paymentDraft.method}
                                    onChange={(e) =>
                                        setPaymentDraft((prev) => ({
                                            ...prev,
                                            method: e.target.value as BookingPaymentMethod,
                                        }))
                                    }
                                >
                                    {Object.entries(BOOKING_PAYMENT_METHOD_LABELS).map(([method, label]) => (
                                        <option key={method} value={method}>
                                            {label}
                                        </option>
                                    ))}
                                </Select>
                                {paymentDraft.method === 'boleto' && (
                                    <HelperText>O boleto pode levar até 3 dias úteis para ser compensado.</HelperText>
                                )}
                            </>
                        )}
                    </InputGroup>
                    {paymentDraft.percentage !== null && (
                        <InputGroup>
                            <CheckboxLabel>
                                <Checkbox
                                    checked={paymentDraft.timeoutMinutes !== null}
                                    onChange={(e) =>
                                        setPaymentDraft((prev) => ({
                                            ...prev,
                                            timeoutMinutes: e.target.checked ? (prev.timeoutMinutes ?? 30) : null,
                                        }))
                                    }
                                />
                                Cancelar se não pagar no prazo
                            </CheckboxLabel>
                            {paymentDraft.timeoutMinutes !== null && (
                                <>
                                    <LabelRow>Minutos</LabelRow>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={paymentDraft.timeoutMinutes}
                                        onChange={(e) =>
                                            setPaymentDraft((prev) => ({
                                                ...prev,
                                                timeoutMinutes: Number(e.target.value),
                                            }))
                                        }
                                    />
                                </>
                            )}
                        </InputGroup>
                    )}
                    {paymentDraft.percentage !== null && (
                        <InputGroup>
                            <CheckboxLabel>
                                <Checkbox
                                    checked={paymentDraft.transferDeadlineHours !== null}
                                    onChange={(e) =>
                                        setPaymentDraft((prev) => ({
                                            ...prev,
                                            transferDeadlineHours: e.target.checked
                                                ? (prev.transferDeadlineHours ?? 24)
                                                : null,
                                        }))
                                    }
                                />
                                Permitir transferência/estorno do sinal
                            </CheckboxLabel>
                            {paymentDraft.transferDeadlineHours !== null && (
                                <>
                                    <LabelRow>Horas de antecedência mínima</LabelRow>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={paymentDraft.transferDeadlineHours}
                                        onChange={(e) =>
                                            setPaymentDraft((prev) => ({
                                                ...prev,
                                                transferDeadlineHours: Number(e.target.value),
                                            }))
                                        }
                                    />
                                    <HelperText>
                                        O cliente pode transferir o sinal ao remarcar (mesmos serviços) ou receber
                                        estorno ao cancelar, desde que com a antecedência configurada.
                                    </HelperText>
                                </>
                            )}
                        </InputGroup>
                    )}
                </FormGroup>
            </Modal>

            <Modal
                isOpen={commercialInfoModal.isOpen}
                onClose={commercialInfoModal.close}
                title="Atualizar dados cadastrais"
                footer={
                    <>
                        <Button onClick={commercialInfoModal.close} disabled={isSaving}>
                            Cancelar
                        </Button>
                        <Button primary onClick={saveCommercialInfo} disabled={!canSaveCommercialInfo}>
                            {isSaving ? 'Salvando...' : 'Confirmar dados'}
                        </Button>
                    </>
                }
            >
                {isLoadingCommercialInfo ? (
                    <FormGroup>
                        <span style={{ color: '#888' }}>Carregando dados cadastrais...</span>
                    </FormGroup>
                ) : commercialInfoDraft ? (
                    <FormGroup>
                        <span style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
                            Confirme ou atualize seus dados cadastrais para manter sua conta de pagamentos ativa. Esta
                            verificação é uma exigência regulatória anual do Banco Central.
                        </span>
                        <InputGroup>
                            <LabelRow>E-mail</LabelRow>
                            <Input
                                type="email"
                                value={commercialInfoDraft.email}
                                onChange={(e) =>
                                    setCommercialInfoDraft((prev) => ({ ...prev!, email: e.target.value }))
                                }
                                placeholder="email@exemplo.com"
                            />
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>Data de nascimento</LabelRow>
                            <Input
                                type="date"
                                value={commercialInfoDraft.birthDate}
                                onChange={(e) =>
                                    setCommercialInfoDraft((prev) => ({ ...prev!, birthDate: e.target.value }))
                                }
                            />
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>Celular</LabelRow>
                            <Input
                                value={formatPhone(commercialInfoDraft.mobilePhone)}
                                onChange={(e) =>
                                    setCommercialInfoDraft((prev) => ({
                                        ...prev!,
                                        mobilePhone: onlyDigits(e.target.value).slice(0, 11),
                                    }))
                                }
                                placeholder="(00) 00000-0000"
                                maxLength={15}
                            />
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>CEP</LabelRow>
                            <Input
                                value={onlyDigits(commercialInfoDraft.postalCode).replace(/(\d{5})(\d)/, '$1-$2')}
                                onChange={(e) =>
                                    setCommercialInfoDraft((prev) => ({
                                        ...prev!,
                                        postalCode: onlyDigits(e.target.value).slice(0, 8),
                                    }))
                                }
                                placeholder="00000-000"
                                maxLength={9}
                            />
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>Endereço</LabelRow>
                            <Input
                                value={commercialInfoDraft.address}
                                onChange={(e) =>
                                    setCommercialInfoDraft((prev) => ({ ...prev!, address: e.target.value }))
                                }
                                placeholder="Rua, Avenida..."
                            />
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>Número</LabelRow>
                            <Input
                                value={commercialInfoDraft.addressNumber}
                                onChange={(e) =>
                                    setCommercialInfoDraft((prev) => ({
                                        ...prev!,
                                        addressNumber: e.target.value,
                                    }))
                                }
                                placeholder="123"
                            />
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>Bairro</LabelRow>
                            <Input
                                value={commercialInfoDraft.province}
                                onChange={(e) =>
                                    setCommercialInfoDraft((prev) => ({ ...prev!, province: e.target.value }))
                                }
                                placeholder="Bairro"
                            />
                        </InputGroup>
                        <InputGroup>
                            <LabelRow>Renda mensal</LabelRow>
                            <Input
                                type="number"
                                min="1"
                                value={commercialInfoDraft.incomeValue}
                                onChange={(e) =>
                                    setCommercialInfoDraft((prev) => ({ ...prev!, incomeValue: e.target.value }))
                                }
                                placeholder="5000"
                            />
                        </InputGroup>
                    </FormGroup>
                ) : null}
            </Modal>

            <ConfirmDialog
                isOpen={showReplaceAccountConfirm}
                title="Conta Asaas"
                message="Existem clientes vinculados à conta Asaas atual. Ao prosseguir, esses vínculos serão removidos."
                confirmText="Continuar"
                loadingText="Salvando..."
                onConfirm={handleConfirmReplaceAccount}
                onCancel={() => setShowReplaceAccountConfirm(false)}
                isLoading={isSaving}
            />
        </ConfigContainer>
    );
}
